'use client';

import { useCallback, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';

import { useResetPasswordQuery } from '@shared/api';
import { Locale, ModalContext } from '@shared/libs';
import { Button, ButtonColor, Input } from '@shared/ui';

import styles from './recovery-pass-form.module.css';

interface Auth {
  email: string;
}

interface LoginFormProps {
  onClose?: () => void;
  locale: Locale;
}

export const RecoveryPassForm = ({ onClose, locale }: LoginFormProps) => {
  const { register, handleSubmit } = useForm<Auth>();
  const openModal = useContext(ModalContext);

  const { mutate, error, isError, isSuccess, data, isLoading, status } = useResetPasswordQuery();

  useEffect(() => {
    if (isSuccess && data) {
      onClose?.();
      openModal({
        modal: {
          title: locale.PAGE_PERSONAL_RECOVER_PASS_SUCCESS || 'PAGE_PERSONAL_RECOVER_PASS_SUCCESS',
          // eslint-disable-next-line react/no-unstable-nested-components
          Content: ({ onClose: onCloseModal }) => (
            <>
              <p className={styles.text}>
                {locale.PAGE_PERSONAL_RECOVER_PASS_SUCCESS_TEXT ||
                  'PAGE_PERSONAL_RECOVER_PASS_SUCCESS_TEXT'}
              </p>
              <Button
                type="button"
                color={ButtonColor.GREEN}
                className={styles.btn}
                loading={isLoading}
                onClick={onCloseModal}
              >
                {locale.PAGE_PERSONAL_RECOVER_PASS_SUCCESS_BTN ||
                  'PAGE_PERSONAL_RECOVER_PASS_SUCCESS_BTN'}
              </Button>
            </>
          ),
        },
      });
    }
  }, [isSuccess, isLoading, openModal, locale, data, status, onClose]);

  const submit = useCallback(
    ({ email }: Auth) => {
      mutate({ email });
    },
    [mutate],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form className={styles.form} onSubmit={handleSubmit(submit)}>
      <Input
        placeholder={
          locale.PAGE_PERSONAL_RECOVER_PASS_MAIL_PH || 'PAGE_PERSONAL_RECOVER_PASS_MAIL_PH'
        }
        autoComplete="email"
        label={locale.PAGE_PERSONAL_RECOVER_PASS_MAIL || 'PAGE_PERSONAL_RECOVER_PASS_MAIL'}
        formGroupClassName={styles.formGroup}
        className={styles.input}
        {...register('email', { required: true })}
      />

      {isError && (
        <p
          className={styles.error}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html:
              error?.response?.data?.error?.msg ||
              locale.PAGE_PERSONAL_RECOVER_PASS_ERROR ||
              'PAGE_PERSONAL_RECOVER_PASS_ERROR',
          }}
        />
      )}

      <Button type="submit" color={ButtonColor.GREEN} className={styles.btn} loading={isLoading}>
        {locale.PAGE_PERSONAL_RECOVER_PASS_BTN || 'PAGE_PERSONAL_RECOVER_PASS_BTN'}
      </Button>
    </form>
  );
};
